import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * CUSTOM HOOK: useConnectionStatus (Optimized)
 *
 * Fixed issues:
 * - Memory leaks from uncleaned intervals
 * - Multiple simultaneous connection checks
 * - Excessive retry attempts
 * - Event listeners not properly removed
 *
 * Improvements:
 * - Proper cleanup of all timers and listeners
 * - Debounced connection checks
 * - AbortController for cancellable requests
 * - Reduced retry frequency
 */

type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'error' | 'timeout' | 'schema_missing';

export const useConnectionStatus = () => {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [lastError, setLastError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastSuccessfulConnection, setLastSuccessfulConnection] = useState<number | null>(null);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);

  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initialCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const backoffTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isCheckingRef = useRef(false);

  const checkConnection = useCallback(async () => {
    if (isCheckingRef.current) {
      console.log('[Connection] Check already in progress, skipping');
      return false;
    }

    isCheckingRef.current = true;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const checkStartTime = performance.now();

    try {
      setStatus('connecting');
      console.log('[Connection] Starting connection check...');
      
      // Create a promise that will timeout after 10 seconds
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Connection timeout after 10 seconds'));
        }, 10000);
      });
      
      const queryPromise = supabase
        .from('databases')
        .select('id')
        .limit(1)
        .abortSignal(abortControllerRef.current.signal);
      
      try {
        // Race between the query and timeout
        const result = await Promise.race([queryPromise, timeoutPromise]) as any;
        const { data, error } = result;
        const checkDuration = Math.round(performance.now() - checkStartTime);

        if (error) {
          console.error('[Connection] Query error:', error);

          // Check if it's a table doesn't exist error
          if (error.message?.includes('relation "databases" does not exist') ||
              error.message?.includes('table "databases" does not exist') ||
              error.code === 'PGRST116') {
            setStatus('schema_missing');
            setLastError('Database schema not found. Please run migrations in Supabase SQL Editor.');
            setConsecutiveFailures(prev => prev + 1);
            console.error('[Connection] Schema missing error');
            return false;
          }

          // Other database errors
          throw error;
        }

        // Connection successful
        console.log(`[Connection] ✓ Connected successfully in ${checkDuration}ms`);
        setStatus('connected');
        setLastError(null);
        setLastSuccessfulConnection(Date.now());
        setConsecutiveFailures(0);
        isCheckingRef.current = false;
        return true;
        
      } catch (error) {
        // Handle timeout error
        if (error instanceof Error && error.message === 'Connection timeout after 10 seconds') {
          console.error('[Connection] ✗ Timeout after 10 seconds');
          setStatus('timeout');
          setLastError('Connection timeout. Supabase may be slow or unavailable. Please wait and try again.');
          setConsecutiveFailures(prev => prev + 1);
          isCheckingRef.current = false;
          return false;
        }

        throw error;
      }
      
    } catch (error) {
      const checkDuration = Math.round(performance.now() - checkStartTime);
      console.error(`[Connection] ✗ Error after ${checkDuration}ms:`, error);

      // Handle different types of errors
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') ||
            error.message.includes('fetch') ||
            error.message.includes('NetworkError') ||
            error.message.includes('ERR_NETWORK') ||
            error.message.includes('ERR_INTERNET_DISCONNECTED')) {
          setStatus('disconnected');
          setLastError('Network error. Check your internet connection.');
          console.error('[Connection] Network error detected');
        } else if (error.message.includes('Invalid API key') ||
                   error.message.includes('unauthorized') ||
                   error.message.includes('Invalid JWT') ||
                   error.message.includes('JWT expired') ||
                   error.message.includes('JWT')) {
          setStatus('error');
          setLastError('Authentication error. Check VITE_SUPABASE_ANON_KEY in .env file.');
          console.error('[Connection] Auth error detected');
        } else if (error.message.includes('relation') && error.message.includes('does not exist')) {
          setStatus('schema_missing');
          setLastError('Database schema not found. Run migrations in Supabase.');
          console.error('[Connection] Schema missing error');
        } else if (error.message.includes('ENOTFOUND') ||
                   error.message.includes('getaddrinfo') ||
                   error.message.includes('DNS')) {
          setStatus('error');
          setLastError('DNS error. Check VITE_SUPABASE_URL in .env file.');
          console.error('[Connection] DNS error detected');
        } else {
          setStatus('error');
          setLastError(`Connection error: ${error.message}`);
          console.error('[Connection] Unknown error:', error.message);
        }
      } else {
        setStatus('error');
        setLastError('Unknown connection error. Check browser console for details.');
        console.error('[Connection] Non-Error object thrown:', error);
      }

      setConsecutiveFailures(prev => prev + 1);
      isCheckingRef.current = false;
      return false;
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  const retryConnection = useCallback(async () => {
    if (isRetrying) return;
    
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    console.log(`[Connection] Retry attempt ${retryCount + 1}`);
    
    const success = await checkConnection();
    
    setIsRetrying(false);
    
    if (!success && retryCount < 2) { // Further reduced retry attempts
      // Exponential backoff for retries
      const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
      
      console.log(`[Connection] Scheduling retry in ${delay}ms`);
      setTimeout(retryConnection, delay);
    } else if (!success && retryCount >= 2) {
      console.log('[Connection] Max retry attempts reached');
      // Set a more helpful error message after max retries
      setLastError('Unable to connect to Supabase after multiple attempts. Please verify your configuration and try again later.');
    }
  }, [checkConnection, isRetrying, retryCount]);

  useEffect(() => {
    console.log('[Connection] Initializing connection monitor');

    const handleOnline = () => {
      console.log('[Connection] Network connection restored');
      setConsecutiveFailures(0);
      setRetryCount(0);
      checkConnection();
    };

    const handleOffline = () => {
      console.log('[Connection] Network connection lost');
      setStatus('disconnected');
      setLastError('Network connection lost');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    initialCheckTimerRef.current = setTimeout(() => {
      checkConnection();
    }, 2000);

    checkIntervalRef.current = setInterval(() => {
      if (status !== 'connected' && !isRetrying && !isCheckingRef.current) {
        console.log('[Connection] Performing periodic connection check');
        checkConnection();
      }

      if (status === 'connected' && lastSuccessfulConnection) {
        const timeSinceLastSuccess = Date.now() - lastSuccessfulConnection;
        if (timeSinceLastSuccess > 600000) {
          console.log('[Connection] Performing health check after 10 minutes');
          checkConnection();
        }
      }
    }, 120000);

    return () => {
      console.log('[Connection] Cleaning up connection monitor');
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (initialCheckTimerRef.current) {
        clearTimeout(initialCheckTimerRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (backoffTimerRef.current) {
        clearTimeout(backoffTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      isCheckingRef.current = false;
    };
  }, [checkConnection, status, isRetrying, lastSuccessfulConnection]);
  
  useEffect(() => {
    if (backoffTimerRef.current) {
      clearTimeout(backoffTimerRef.current);
    }

    if (consecutiveFailures > 1 && !isRetrying && status !== 'schema_missing' && !isCheckingRef.current) {
      const backoffDelay = Math.min(3000 * Math.pow(2, consecutiveFailures - 1), 30000);
      console.log(`[Connection] Multiple failures detected. Backing off for ${backoffDelay}ms before next retry`);

      backoffTimerRef.current = setTimeout(() => {
        console.log('[Connection] Attempting recovery after backoff');
        retryConnection();
      }, backoffDelay);
    }

    return () => {
      if (backoffTimerRef.current) {
        clearTimeout(backoffTimerRef.current);
      }
    };
  }, [consecutiveFailures, isRetrying, retryConnection, status]);

  return {
    status,
    isConnected: status === 'connected',
    isConnecting: status === 'connecting',
    isDisconnected: status === 'disconnected' || status === 'error' || status === 'timeout',
    isSchemaMissing: status === 'schema_missing',
    lastError,
    retryConnection,
    isRetrying,
    consecutiveFailures,
    lastSuccessfulConnection
  };
};